import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { uniqueId } from 'lodash';
import { Tooltip } from 'react-tooltip';
import clsx from 'clsx';
import Tippy from '@tippyjs/react';
import { ChevronDown, ChevronUp } from 'react-feather';
import Collapsible from 'react-collapsible';
import dayjs from 'plugins/dayjs';
// redux
import { batch, connect } from 'react-redux';
import { mapDispatchProps, mapStateToProps } from 'store/store';
import { ROUTES } from 'routes';
// components
import { useLanguage, getLanguage, APP_LOCALE } from '@languages/index';
import API from 'api';
import { GuidelineRuleDto } from 'api/repositories/guidelineRules/guidelineRules.types';
import { isGlovv, isGlovvJp, isGlovvKr } from 'constants/domains/domains';
import GUIDELINE_RULES from 'constants/guidelineRules';
import { LOCALES } from 'constants/locales';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import STORE from 'constants/store';
import URLS from 'constants/urls';
import * as HELPER_CURRENCY from 'helpers/currency';
import { debounce } from 'helpers/debounce';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import HELPER_UI from 'helpers/ui';
import Button from 'components/button';
import RadioButton from 'components/radioButton';
import Checkbox, { ICON_TYPES } from 'components/checkbox';
import Paginator from 'components/paginator';
// pages
import PaymentComplete from 'pages/payments/complete';
import SeedingWithCommissionsPanel from 'pages/notification-panel/SeedingWithCommissionsPanel';
import ApplyCommissionModal from './modal/applyCommissionModal';
import TrackingNumberGuideModal from './modal/trackingNumberGuideModal';

export const CampaignStatus = {
  // 준비중
  READY: 1,
  // 모집 시작
  RECRUIT_STARTED: 2,
  // 모집 종료
  RECRUIT_ENDED: 3,
  // 선정 시작
  SELECTION_STARTED: 4,
  // 선정 종료
  SELECTION_ENDED: 5,
  // 출고 시작
  PRODUCT_DEPLOY_STARTED: 6,
  // 포스팅 시작
  POSTING_STARTED: 7,
  // 포스팅 종료
  POSTING_ENDED: 8,
  // 완료
  COMPLETED: 9,
};

function calculateTimeLeft(regDt) {
  const targetDate = dayjs(regDt);
  const now = dayjs();

  let timeLeft = {};
  if (targetDate.isAfter(now)) {
    timeLeft = {
      days: targetDate.diff(now, 'day'),
      hours: (targetDate.diff(now, 'hour') % 24).toString().padStart(2, '0'),
      minutes: (targetDate.diff(now, 'minute') % 60)
        .toString()
        .padStart(2, '0'),
      seconds: (targetDate.diff(now, 'second') % 60)
        .toString()
        .padStart(2, '0'),
      ms: (targetDate.diff(now, 'millisecond') % 100)
        .toString()
        .padStart(2, '0'),
    };
  }
  return timeLeft;
}

const CampaignIndex = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const location = useLocation();

  const options = [
    { name: languageTexts.option_1, value: '' },
    { name: languageTexts.option_2, value: 'closed' },
  ];

  const ORDER_TYPES = {
    APPLICANTS: 'applicants_count_desc',
    CREATED_AT: '',
  };

  /* >>> STATES */

  const navigate = useNavigate();

  const isBeautyCategoryBrand = props.brand?.isCategoryBeauty;
  const beautyIntroPageUrl =
    process.env.LOCALE == LOCALES.JA
      ? `https://glovv.co.kr/japan`
      : `https://glovv.co.kr/?isVisibleContactForm=0`;

  // input for fetch
  const currentPage = useRef(1);
  const [currentCampaignTab, setCurrentCampaignTab] = useState(options[0]);
  const [selectedOrder, setSelectedOrder] = useState(ORDER_TYPES.CREATED_AT);
  const [filterNotSelected, setFilterNotSelected] = useState(false);
  const [filterNotDeployed, setFilterNotDeployed] = useState(false);
  const [filterTrackingNumberEmpty, setFilterTrackingNumberEmpty] =
    useState(false);

  // fetched data
  const totalProductsCount = useRef(0);
  const [products, setProducts] = useState([]);
  const [paginator, setPaginator] = useState<any>(undefined);
  const [dates, setDates] = useState([]);
  const [filterCounts, setFilterCounts] = useState([0, 0, 0]);
  // others
  const [selected, setSelected] = useState<any[]>([]);
  const [isFetching, setIsFetching, isContentEnded, setIsContentEnded] =
    useInfiniteScroll(async () => {
      if (isContentEnded) return false;
      // await fetchProducts();
    });
  const [timeLeft, setTimeLeft] = React.useState(
    calculateTimeLeft(
      dayjs(props.brand.regDt).add(2, 'day').format('YYYY-MM-DD'),
    ),
  );
  // files
  const fileInputRef = useRef<any>([]);
  const fileToUpload = useRef<any>([]);

  /* >>> USE_EFFECTS */

  useEffect(() => {
    const id = setTimeout(() => {
      if (
        props.brand.isSeedingPassWelcomeBonusEnabled == true &&
        props.brand.hasExperiencedSeedingPass == false &&
        !products.length
      ) {
        setTimeLeft(
          calculateTimeLeft(
            dayjs(props.brand.regDt).add(2, 'day').format('YYYY-MM-DD'),
          ),
        );
      } else {
        clearTimeout(id);
      }
    }, 10);

    if (
      props.brand.isSeedingPassWelcomeBonusEnabled == false ||
      props.brand.hasExperiencedSeedingPass == true
    ) {
      clearTimeout(id);
    }

    return () => {
      clearTimeout(id);
    };
  }, [timeLeft]);

  useEffect(() => {
    (async () => {
      try {
        setIsFetching(true);
        let config: any = { refresh: true };
        const pathCache =
          props?.pathCaches &&
          props?.pathCaches[STORE.PATH_CACHES_KEYS.CAMPAIGN_INDEX];

        if (pathCache?.data != null) {
          setDates([]);
          setIsContentEnded(false);
          config = mountStatesAndMakeConfigWithCache(pathCache);
        }

        await Promise.all([fetchProducts(config), fetchProductCounts(config)]);
        await new Promise((resolve) => setTimeout(() => resolve(0), 50));
        moveToScrollWithCache(pathCache);
        props.removePathCache({ key: STORE.PATH_CACHES_KEYS.CAMPAIGN_INDEX });
      } catch (e) {
        props.removePathCache({ key: STORE.PATH_CACHES_KEYS.CAMPAIGN_INDEX });
      }
    })();
  }, []);

  /* >>> API REQUESTS */
  const fetchProducts = async (config?) => {
    setProducts([]);
    setIsFetching(true);

    if (config?.refresh) {
      setDates([]);
      setIsContentEnded(false);
    }

    let targetPage = config?.page ? config.page : currentPage.current;

    if (config?.refresh) {
      targetPage = 1;
    }

    let params = {
      withs: [
        'applicants_product_not_prepared',
        'campaign.preferred_video_type_guideline_rules',
      ],
      type: 'PRODUCT',
      brandId: props.brand.id,
      brandToken: props.auth.token,
      isCampaign: true,
      isCampaignCompleted: currentCampaignTab?.value === 'closed' ? 1 : 0,
      filterNotSelected: filterNotSelected ? 1 : 0,
      filterNotDeployed: filterNotDeployed ? 1 : 0,
      filterTrackingNumberEmpty: filterTrackingNumberEmpty ? 1 : 0,
      page: targetPage,
      size: 20,
      orderBy:
        selectedOrder == ORDER_TYPES.APPLICANTS
          ? 'applicants_count_desc'
          : undefined,
      ...config,
    };

    await API.Product.getProductsV2({
      brandID: props.brand.id,
      brandToken: props.auth.token,
      orderBy: config.orderBy ?? undefined,
      isCampaignCompleted: currentCampaignTab?.value === 'closed' ? 1 : 0,
      filterNotSelected: filterNotSelected ? 1 : 0,
      filterNotDeployed: filterNotDeployed ? 1 : 0,
      page: targetPage,
      size: 20,
      // NOTE: isCampaignCompleted, filterNotSelected, filterNotDeployed 가 초기에 세팅안되고 한번 로딩 된이후로 세팅, 초기는 config 에서, 그 이후는 위 값으로 설정됨
      // NOTE: 따라서, 추후 리팩토링 필요함
      ...config,
    })
      .then((response) => {
        currentPage.current = targetPage;

        const modifiedProducts = response.data.products.map((item) => ({
          ...item,
          createdDate: dayjs(item.regDt).format('YYYY-MM-DD'),
        }));

        const _paginator = response.data.paginator;

        setDates((prevDates) => {
          const uniqueDates = new Set([
            ...modifiedProducts.map((product) => product.createdDate),
          ]);
          return Array.from(uniqueDates);
        });

        const result = modifiedProducts;
        if (!filterNotSelected && !filterNotDeployed) {
          totalProductsCount.current = _paginator.total;
        }

        setProducts((prevProducts) => {
          const uniqueProducts = new Set([...result]);
          return Array.from(uniqueProducts);
        });
        setPaginator(_paginator);
        setIsContentEnded(_paginator.currentPage >= _paginator.lastPage);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const fetchProductCounts = async (config?) => {
    await API.Product.counts({
      brandToken: props.auth.token,
      isCampaign: true,
      isCampaignCompleted: currentCampaignTab?.value === 'closed' ? 1 : 0,
      ...config,
    })
      .then((response) => {
        const countNotSelected = response.data?.countNotSelected ?? 0;
        const countNotDeployed = response.data?.countNotDeployed ?? 0;
        const countTrackingNumberEmpty = response.data?.countTrackingNumberEmpty ?? 0;
        setFilterCounts([
          countNotSelected,
          countNotDeployed,
          countTrackingNumberEmpty,
        ]);
      })
      .catch((e) => {
        console.log('count error', e);
        setFilterCounts([0, 0, 0]);
      });
  };

  /* >>> CACHE */
  /**
   *
   * @param pathCache
   * @return config for fetchProducts and fetchProductCounts
   */
  function mountStatesAndMakeConfigWithCache(pathCache) {
    let config = {};

    if (pathCache?.data != null) {
      for (let dataKey of Object.keys(pathCache.data)) {
        const dataValue = pathCache.data[dataKey];
        switch (dataKey) {
          case 'currentCampaignTab':
            setCurrentCampaignTab(dataValue);
            config['isCampaignCompleted'] =
              dataValue?.value === 'closed' ? 1 : 0;
            break;
          case 'currentPage':
            currentPage.current = dataValue;
            break;
          case 'filterNotDeployed':
            setFilterNotDeployed(dataValue);
            config['filterNotDeployed'] = dataValue ? 1 : 0;
            break;
          case 'filterNotSelected':
            setFilterNotSelected(dataValue);
            config['filterNotSelected'] = dataValue ? 1 : 0;
            break;
          case 'selectedOrder':
            setSelectedOrder(dataValue);
            config['orderBy'] =
              dataValue == ORDER_TYPES.APPLICANTS
                ? 'applicants_count_desc'
                : undefined;
            break;
          default:
            break;
        }
      }
    }

    return config;
  }

  function moveToScrollWithCache(pathCache) {
    if (pathCache?.scrollTop != null) {
      window.scrollTo(0, pathCache.scrollTop);
    }
  }

  const saveCurrentState = (pathDestination) => {
    props.addPathCache({
      key: STORE.PATH_CACHES_KEYS.CAMPAIGN_INDEX,
      allowedPathnames: [
        location.pathname,
        ROUTES.CAMPAIGN_INDEX.PATH,
        pathDestination,
      ],
      scrollTop: window.pageYOffset || document.documentElement.scrollTop,
      data: {
        currentPage: currentPage.current,
        currentCampaignTab: currentCampaignTab,
        selectedOrder: selectedOrder,
        filterNotSelected: filterNotSelected,
        filterNotDeployed: filterNotDeployed,
      },
    });
  };

  /* >>> OTHER FUNCTIONS */

  // const TimerComponents = () => {
  //   if (!timeLeft) {
  //     return null;
  //   }

  //   return (
  //     <span className="text-[#000000] font-bold text-[2.25rem] tracking-tighter">
  //       {`${timeLeft["days"]}일 ${timeLeft["hours"]}:${timeLeft["minutes"]}:${timeLeft["seconds"]}:${timeLeft["ms"]}`}
  //     </span>
  //   );
  // };

  const handleExcelChange = (event?: any) => {
    fileToUpload.current = event?.target?.files.length
      ? event?.target?.files[0]
      : null;

    if (!fileToUpload.current) {
      props.showModal({ text: languageTexts.select_file_plz });
      return false;
    }

    API.CampaignApplicants.importApplicantsData({
      brandToken: props.auth.token,
      file: fileToUpload.current,
    })
      .then((response) => {
        props.showModal({ text: response.message });
      })
      .catch((error) => {
        const lines = error.data.failed.split(',');

        props.showModal({
          title: languageTexts.error,
          text: languageTexts._excelError1(lines),
        });
      })
      .finally(() => {
        fileInputRef.current.value = '';
      });
  };

  const filterCampaigns = (campaigns, config?) => {
    if (filterNotSelected) {
      campaigns.forEach((campaign) => {
        campaign.products = campaign.products.filter((product) => {
          return product.selectedAt ? false : true;
        });
      });
    }

    if (filterNotDeployed) {
      campaigns.forEach((campaign) => {
        campaign.products = campaign.products.filter((product) => {
          return product.deployedAt ? false : true;
        });
      });
    }

    if (config.isCompleted) {
      return (campaigns = campaigns.filter((campaign) => {
        return campaign.status == CampaignStatus.COMPLETED;
      }));
    } else {
      return (campaigns = campaigns.filter((campaign) => {
        return campaign.status != CampaignStatus.COMPLETED;
      }));
    }
  };

  const generateCampaignList = (campaigns: any) => {
    const list = [];
    campaigns.current.forEach((campaign) => {
      const createdAtDate = dayjs(campaign.createdAt).format('YYYY-MM-DD');
      if (!list.filter((item) => item.date == createdAtDate).length) {
        list.push({
          date: createdAtDate,
          campaigns: [],
        });
      }
    });

    campaigns.current.forEach((campaign) => {
      const createdAtDate = dayjs(campaign.createdAt).format('YYYY-MM-DD');
      list.forEach((date) => {
        if (createdAtDate === date.date) {
          if (!date['campaigns']) date['campaigns'] = [];
          date['campaigns'].push(campaign);
        }
      });
    });

    return list;
  };

  const renderGlovvReferenceBanners = () => {
    const RESOURCE_CACHE_KEY = '2510101656';

    if (isGlovvKr()) {
      return (
        <div className="mt-[30px] flex flex-col">
          <div className="w-full">
            <a href="https://bit.ly/432h3b2">
              <img src={URLS.CDN.BRAND.IMAGES.BANNERS.URL + '/kr/' + 'banner-guidebook-reels.webp' + `?v=${RESOURCE_CACHE_KEY}`} />
            </a>
          </div>
          <div className="grid grid-cols-2">
            {[
              {
                resourceKey: 'banner-reference-base-color.webp',
                linkUrl: 'https://bit.ly/3WAsBys',
              },
              {
                resourceKey: 'banner-reference-hair-body.webp',
                linkUrl: 'https://bit.ly/4o2Rv6l',
              },
              {
                resourceKey: 'banner-reference-perfume-diffuser.webp',
                linkUrl: 'https://bit.ly/4378RGn',
              },
              {
                resourceKey: 'banner-reference-food-supplements.webp',
                linkUrl: 'https://bit.ly/46VBUxP',
              },
            ].map((banner, index) => {
              return (
                <div key={index}>
                  <a href={banner.linkUrl}>
                    <img src={URLS.CDN.BRAND.IMAGES.BANNERS.URL + '/kr/' + banner.resourceKey + `?v=${RESOURCE_CACHE_KEY}`} />
                  </a>
                </div>
              )
            })}
          </div>
          <div className="w-full">
            <div className="w-full cursor-pointer" onClick={() => navigate(ROUTES.CAMPAIGN_CREATE.PATH)}>
              <img src={URLS.CDN.BRAND.IMAGES.BANNERS.URL + '/kr/' + 'banner-oliveyoung-dec.webp' + `?v=${RESOURCE_CACHE_KEY}`} />
            </div>
          </div>
        </div>
      );
    } else if (isGlovvJp()) {
      return (
        <div className="mt-[30px]">
          <div className="w-full">
            <a href="https://featpaper.com/l/abUkKRDQ">
              <img src={URLS.CDN.BRAND.IMAGES.BANNERS.URL + '/glovv-reels-banner-ja.webp' + `?v=${RESOURCE_CACHE_KEY}`} />
            </a>
          </div>
        </div>
      );
    }
    
    return null;
  }

  const renderCampaignIndexHeader = () => {
    return (
      <div className="flex flex-row w-full justify-between items-center mt-12 mb-6">
        <div className="flex flex-col">
          <span className="text-2xl font-bold tracking-tighter">{`${
            languageTexts.header_title
          } (${totalProductsCount.current ?? 0})`}</span>
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row">
            {!isGlovv() && (
              <Button
                className={'hidden sm:flex px-10 py-3 items-center'}
                text={languageTexts.header_button_1}
                textClassName="font-bold text-base tracking-tighter text-center"
                borderColor={'transparent'}
                onClick={() => {
                  // dev
                  if (isBeautyCategoryBrand) {
                    window.open(beautyIntroPageUrl, '_blank');
                    return;
                  }
                  navigate(ROUTES.CAMPAIGN_INTRO.PATH);
                }}
              />
            )}
            <Button
              className={'px-7 py-3 items-center'}
              text={`<span class="hidden sm:inline-block">${languageTexts.header_button_2_1}</span> ${languageTexts.header_button_2_2}`}
              textColor={'WHITE'}
              textClassName="font-bold text-base tracking-tighter text-center whitespace-nowrap"
              buttonColor={'BLACK'}
              borderColor={'BLACK'}
              onClick={() => {
                navigate(ROUTES.CAMPAIGN_CREATE.PATH);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderFetchIndicator = () => {
    if (products.length) return null;
    if (!products.length && isFetching) {
      return (
        <div className="w-full h-[3000px]">
          <div className="flex flex-row justify-center text-center my-[120px]">
            <span className="prose prose-xl text-slate-400">
              {languageTexts.loading_1}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-row justify-center text-center my-[120px]">
        {currentCampaignTab && currentCampaignTab.value === 'closed' ? (
          <span className="prose prose-xl text-slate-400">
            {products.length ? '' : languageTexts.fetch_text1}
          </span>
        ) : (
          <span className="prose prose-xl text-slate-400">
            {filterNotDeployed || filterNotSelected ? (
              <>{products.length ? '' : languageTexts.fetch_text2}</>
            ) : (
              <>{products.length ? '' : languageTexts.fetch_text2}</>
            )}
          </span>
        )}
      </div>
    );
  };

  const renderCampaignFilters = () => {
    if (!currentCampaignTab) return null;
    // if (!campaigns.current) return null;

    return (
      <div className="flex flex-col gap-3">
        <RadioButton
          options={options}
          tabClassName={'py-3 bg-gray-100'}
          textClassName={'text-black text-base tracking-tighter'}
          selected={currentCampaignTab}
          selectedClassName={'py-3 bg-black'}
          selectedTextClassName="font-bold text-base tracking-tighter"
          selectedTextColor="WHITE"
          onSelect={(option) => {
            if (isFetching) return false;

            setCurrentCampaignTab(option);
            setDates([]);
            setProducts([]);

            debounce(() => {
              fetchProducts({
                refresh: true,
                isCampaignCompleted: option.value == 'closed' ? 1 : 0,
              });
              fetchProductCounts({
                isCampaignCompleted: option.value == 'closed' ? 1 : 0,
              });
            }, 200);
          }}
        />

        <div className="flex flex-row justify-between py-5">
          <div className="flex flex-row gap-4">
            <Checkbox
              text={`${languageTexts.filter_check_1} (${filterCounts[0]})`}
              checked={filterNotSelected}
              onClick={() => {
                if (isFetching) return false;
                const targetValue = !filterNotSelected;

                setFilterNotSelected(targetValue);
                if (targetValue) {
                  setFilterNotDeployed(false);
                  setFilterTrackingNumberEmpty(false);
                }

                let newFilterNotDeployed = 0;
                if (!targetValue && filterNotDeployed) {
                  newFilterNotDeployed = 1;
                }

                fetchProducts({
                  refresh: true,
                  filterNotSelected: targetValue ? 1 : 0,
                  filterNotDeployed: newFilterNotDeployed,
                });
              }}
            />

            <Checkbox
              text={`${languageTexts.filter_check_2} (${filterCounts[1]})`}
              checked={filterNotDeployed}
              onClick={() => {
                if (isFetching) return false;
                const targetValue = !filterNotDeployed;

                setFilterNotDeployed(targetValue);
                if (targetValue) {
                  setFilterNotSelected(false);
                  setFilterTrackingNumberEmpty(false);
                }

                let newFilterNotSelected = 0;
                if (!targetValue && filterNotSelected) {
                  newFilterNotSelected = 1;
                }

                fetchProducts({
                  refresh: true,
                  filterNotSelected: newFilterNotSelected,
                  filterNotDeployed: targetValue ? 1 : 0,
                });
              }}
            />
          </div>

          <div
            className="upload-excel-help hidden sm:flex flex-row gap-1 hover:font-bold cursor-pointer"
            onClick={() => {
              fileInputRef.current.click();
            }}
          >
            <span className="text-xs tracking-tighter underline">
              {languageTexts.filter_excel_title}
            </span>

            <img
              className="w-[18px] h-[18px]"
              src={`/assets/icons/ic-help-circle.svg`}
            />

            <input
              ref={fileInputRef}
              type={'file'}
              className="hidden"
              multiple={false}
              onChange={handleExcelChange}
            />
          </div>

          <Tooltip anchorSelect=".upload-excel-help" place="top">
            <span className="max-w-[243px]">
              {languageTexts._filterExcelTooltip()}
            </span>
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderCampaignOrderSelect = () => {
    return (
      <div className="flex flex-row ml-auto items-center cursor-pointer text-black gap-1">
        <div className="flex flex-row gap-6 sm:ml-auto">
          <Checkbox
            text={languageTexts.order_check_1}
            checked={selectedOrder == ORDER_TYPES.APPLICANTS}
            type={ICON_TYPES.CIRCLE}
            onClick={() => {
              if (isFetching) return false;
              setSelectedOrder(ORDER_TYPES.APPLICANTS);
              setDates([]);
              debounce(() => {
                fetchProducts({
                  refresh: true,
                  orderBy: ORDER_TYPES.APPLICANTS,
                });
              }, 200);
            }}
          />

          <Checkbox
            text={languageTexts.order_check_2}
            checked={selectedOrder == ORDER_TYPES.CREATED_AT}
            type={ICON_TYPES.CIRCLE}
            onClick={() => {
              if (isFetching) return false;
              setSelectedOrder(ORDER_TYPES.CREATED_AT);
              setDates([]);
              debounce(() => {
                fetchProducts({
                  refresh: true,
                  orderBy: ORDER_TYPES.CREATED_AT,
                });
              }, 200);
            }}
          />
        </div>
      </div>
    );
  };

  const renderCampaignProducts = () => {
    return (
      <div className="flex flex-col gap-5 mx-auto">
        {dates.map((date, index) => {
          if (!products.filter((item) => item.createdDate == date).length) {
            return null;
          }

          return (
            <CampaignDateGroup
              key={`${index}_${date}`}
              date={date}
              navigate={navigate}
              fetchProducts={fetchProducts}
              products={products.filter((item) => item.createdDate == date)}
              saveCurrentState={saveCurrentState}
              selected={selected}
              setSelected={setSelected}
              {...props}
            />
          );
        })}

        {renderFetchIndicator()}
        {renderPaginator()}
      </div>
    );
  };

  const renderExportExcelButton = () => {
    if (!selected || !selected.length) return null;

    return (
      <div
        className={`hidden sm:block fixed bottom-0 left-0 right-0 h-[100px]`}
      >
        <div className="max-w-[430px] h-[52px] mx-auto animate__animated animate__fadeIn">
          <Button
            text={languageTexts._excelButton1(selected.length)}
            buttonColor={'BLACK'}
            textColor={'WHITE'}
            onClick={() => {
              window.open(
                `/api/export/campaign/applicants?brand_token=${
                  props.auth.token
                }&products=${selected.join(',')}`,
                '_blank',
              );
            }}
          />
        </div>
      </div>
    );
  };

  const renderCampaignTrackingNumberEmptyFilter = () => {
    if (props.brand.departureCountry == props.brand.destinationCountry)
      return null;
    if (!filterCounts[2]) return null;

    return (
      <div className="flex flex-row gap-4">
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-row">
            <div className="flex flex-col gap-2">
              <span className="font-bold text-[#FF0000]">
                {languageTexts.tracking_number_required_title}
              </span>
              <span className="text-[#FF0000]">
                {languageTexts.tracking_number_required_description}
              </span>
            </div>
          </div>
          <div className="flex flex-row">
            <Checkbox
              textClassName="text-[#FF0000]"
              text={`${languageTexts.filter_check_3} (${filterCounts[2]})`}
              checked={filterTrackingNumberEmpty}
              onClick={() => {
                if (isFetching) return false;
                const targetValue = !filterTrackingNumberEmpty;

                setFilterTrackingNumberEmpty(targetValue);

                if (targetValue) {
                  setFilterNotDeployed(false);
                  setFilterNotSelected(false);
                }

                fetchProducts({
                  refresh: true,
                  filterNotSelected: 0,
                  filterNotDeployed: 0,
                  filterTrackingNumberEmpty: targetValue ? 1 : 0,
                });
              }}
            />
          </div>
          <div className="flex flex-row">
            <span
              className="underline undeline-[#FF0000] text-[#FF0000] cursor-pointer"
              onClick={() => {
                props.showOverlays({
                  content: () => {
                    return <TrackingNumberGuideModal {...props} />;
                  },
                });
              }}
            >
              {languageTexts.tracking_number_bulk_input_guide}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPaginator = () => {
    if (!paginator) return null;

    return (
      <Paginator
        currentPage={currentPage.current}
        lastPage={paginator.lastPage}
        onPageChange={(page) => {
          fetchProducts({ page: page });
        }}
      />
    );
  };

  return (
    <div className="flex flex-col items-center px-5">
      <div className="w-full max-w-[1024px] pb-40">
        {isGlovv() ? (
          renderGlovvReferenceBanners()
        ) : (
          <div className="w-full pt-[30px]">
            <SeedingWithCommissionsPanel {...props} hideBlockButton={true} />
          </div>
        )}

        {renderCampaignIndexHeader()}
        {renderCampaignFilters()}
        {renderCampaignOrderSelect()}
        {renderCampaignTrackingNumberEmptyFilter()}
        {renderCampaignProducts()}
      </div>
      {renderExportExcelButton()}
    </div>
  );
};

const CampaignStatusDesc = ({ statusDesc }) => {
  const lines = statusDesc.split('<br/>');

  return (
    <span className="text-sm tracking-tighter font-bold break-keep">
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </span>
  );
};

const CampaignProduct = (props) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const { product, preferences, fetchProducts } = props;
  const navigate = useNavigate();

  const handleClick = () => {
    props.onClick ? props.onClick() : null;

    navigate(ROUTES.CAMPAIGN_PRODUCT_SHOW.SHOW(product.campaignId, product.id));
  };

  const renderCampaignStatus = (campaign?: any, product?: any) => {
    if (!campaign) return null;

    switch (campaign.status) {
      case CampaignStatus.RECRUIT_STARTED:
      case CampaignStatus.RECRUIT_ENDED:
        if (product.selectedAt) {
          return (
            <span className="text-sm tracking-tighter font-bold break-keep">
              {languageTexts.product_selected}
            </span>
          );
        }

        return <CampaignStatusDesc statusDesc={campaign.statusDesc} />;

      case CampaignStatus.SELECTION_STARTED:
        if (product.selectedAt) {
          return (
            <span className="text-sm tracking-tighter font-bold break-keep">
              {languageTexts.product_selected}
            </span>
          );
        }

        return (
          <Tippy content={languageTexts.product_selection_started_title}>
            <div className="md:w-[200px] md:min-w-[240px]">
              <Button
                className={
                  'bg-[#121212] bordered-sm justify-center text-center py-3 w-full'
                }
                textClassName="font-bold tracking-tighter text-sm break-keep"
                textColor={'white'}
                text={languageTexts.product_selection_started_button_1}
                buttonColor="black"
                borderColor={'black'}
              />
            </div>
          </Tippy>
        );

      case CampaignStatus.POSTING_STARTED:
        return (
          <span className="text-sm tracking-tighter font-bold break-keep">
            {languageTexts.product_posting_started_title}
          </span>
        );

      case CampaignStatus.PRODUCT_DEPLOY_STARTED:
        if (
          product.applicantsProductNotPrepared != null &&
          product.applicantsProductNotPrepared.length === 0
        ) {
          return (
            <span className="text-sm tracking-tighter font-bold break-keep">
              {languageTexts.product_prepared}
            </span>
          );
        }

      default:
        return <CampaignStatusDesc statusDesc={campaign.statusDesc} />;
    }
  };

  // const renderPayments = () => {
  //   return (
  //     <div
  //       className="h-[64px] flex flex-col justify-center items-center cursor-pointer"
  //       onClick={() => {
  //         props.showOverlays({
  //           content: () => {
  //             return (
  //               <PaymentComplete
  //                 title={
  //                   product.campaign.isSeedingPassEnabled
  //                     ? languageTexts.product_seeding_pass_1
  //                     : languageTexts.payment_history2
  //                 }
  //                 itemType={
  //                   product.campaign.isSeedingPassEnabled
  //                     ? PAYMENT_PRODUCT_TYPE.SEEDING_PASS_HISTORY
  //                     : PAYMENT_PRODUCT_TYPE.SEEDING
  //                 }
  //                 amount={product.campaign.isSeedingPassEnabled ? 1 : product.campaign.totalQty}
  //                 price={
  //                   product.campaign.isSeedingPassEnabled ? props.brand.priceSeedingPass : props.brand.priceSeeding
  //                 }
  //                 onConfirm={() => {
  //                   props.closeOverlays()
  //                 }}
  //               />
  //             )
  //           },
  //         })
  //       }}
  //     >
  //       <span className="text-sm tracking-tighter text-gray-400 underline cursor-pointer whitespace-nowrap">
  //         {languageTexts.check_payment_history}
  //       </span>
  //     </div>
  //   )
  // }

  const renderCommissions = () => {
    const isAvailable =
      product.campaign.status <= CampaignStatus.RECRUIT_STARTED;
    const commissionPrice = product.campaign?.commissionPaymentPrice ?? 0;
    const isCommissionApplied = commissionPrice > 0;

    if (!isAvailable) return null;

    return (
      <div className="flex ml-auto">
        <Button
          className={`px-2 py-2 w-full flex justify-center bg-[#121212] bordered-sm select-none`}
          renderChildren={() => {
            return (
              <div
                className={clsx([
                  'flex flex-col items-center tracking-tighter',
                  isCommissionApplied ? 'text-[#E8E8E8]' : 'text-black',
                ])}
              >
                <span className="text-sm font-bold">
                  {isCommissionApplied
                    ? languageTexts.product_apply_commissions_applied
                    : languageTexts.product_apply_commissions}
                </span>
                {isCommissionApplied && (
                  <span className="text-xs">
                    {commissionPrice.toLocaleString()}
                    {HELPER_CURRENCY.getCurrencySuffixByBrand(props.settings)}
                  </span>
                )}
              </div>
            );
          }}
          buttonColor="white"
          borderColor={isCommissionApplied ? '#E8E8E8' : 'black'}
          onClick={() => {
            if (isCommissionApplied) {
              props.showModal({
                text: languageTexts.product_apply_commissions_already_applied,
              });
              return false;
            }

            props.showOverlays({
              zIndex: 1081,
              content: () => {
                return (
                  <ApplyCommissionModal
                    {...props}
                    product={product}
                    onSubmit={() => {
                      fetchProducts({ refresh: true });
                    }}
                  />
                );
              },
            });
          }}
        />
      </div>
    );
  };

  const renderDesktopProduct = () => {
    const commissionPrice = product?.campaign?.commissionPaymentPrice ?? 0;

    return (
      <>
        <div
          className={clsx(
            'flex flex-row gap-6 items-center py-5 px-5 border border-gray-200 rounded',
          )}
          key={`product${product.id}`}
        >
          <div className="w-[30px] flex flex-col gap-1 items-center">
            {product.selectedAt ? (
              <>
                <Checkbox
                  checked={props.selected.includes(product.id)}
                  onClick={() => {
                    props.onSelect ? props.onSelect(product) : null;
                  }}
                />
                <span className="font-xs text-[#999999]">
                  {languageTexts.excel}
                </span>
              </>
            ) : (
              <div className="w-[30px]">{/* FOR UX */}</div>
            )}
          </div>

          <div
            className="flex flex-col h-full min-h-[64px] justify-center items-center cursor-pointer"
            onClick={handleClick}
          >
            <div className="w-[64px] h-[64px] overflow-hidden rounded">
              <img className="w-full h-full" src={product.thumbnailSmUrl} />
            </div>
          </div>

          <div
            className="flex flex-col h-full min-h-[64px] justify-center items-center min-w-[160px] w-[160px] cursor-pointer"
            onClick={handleClick}
          >
            {commissionPrice > 0 ? (
              // <span className="text-sm tracking-tighter w-[160px] truncate text-[#FF0099]">
              <span className="text-sm tracking-tighter w-[160px] truncate text-transparent bg-clip-text bg-gradient-to-r from-[#FA7035] to-[#FF0099]">
                {`${languageTexts.campaign_with_commissions}` +
                  ` (${HELPER_CURRENCY.makeMoneyText(
                    commissionPrice, 10000,
                    (moneyText) => languageTexts._price(moneyText, props.settings.currencyText),
                    (moneyText) => languageTexts._price_10000(moneyText, props.settings.currencyText),
                  )})`}
              </span>
            ) : null}
            <span className="text-sm tracking-tighter w-[160px] whitespace-pre-wrap">
              {HELPER_UI.makeProductName(
                product.name,
                product?.campaign?.preferredVideoTypeGuidelineRules,
                product?.campaign
              )}
            </span>
          </div>

          <div
            className="flex flex-col h-full min-h-[64px] justify-center items-center min-w-[160px] w-[160px] cursor-pointer"
            onClick={handleClick}
          >
            <div className="w-[160px]">
              <span className="text-sm tracking-tighter truncate whitespace-pre-wrap">{`${product.colorName} `}</span>
              {props.brand.isCategoryFashion ? (
                <span className="text-sm tracking-tighter truncate whitespace-pre-wrap">{`/ ${product.sizeName} `}</span>
              ) : null}
              <span className="text-sm tracking-tighter truncate whitespace-pre-wrap">{`/ ${languageTexts._amount(
                product.qty,
              )} `}</span>
            </div>
          </div>

          {/* {renderPayments()} */}

          {!product.selectedAt && (
            <div
              className="flex flex-col min-h-[64px] justify-center items-center cursor-pointer text-center"
              onClick={handleClick}
            >
              <span className="text-sm tracking-tighter">{`${languageTexts.applicants_count} : ${props.brand.isCategoryBeauty ? product.applicantsCountBeauty : product.applicantsCount}`}</span>
            </div>
          )}

          {renderCommissions()}

          <div className="flex flex-col min-h-[64px] justify-center items-center h-full ml-auto text-center">
            <div className="cursor-pointer" onClick={handleClick}>
              {renderCampaignStatus(product.campaign, product)}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderMobileProduct = () => {
    const commissionPrice = product?.campaign?.commissionPaymentPrice ?? 0;

    return (
      <div
        className="flex flex-col py-4 px-4 border border-gray-200 rounded"
        key={`product${product.id}`}
      >
        <div className="flex flex-row flex-nowrap items-start justify-between">
          <div
            className="flex flex-row items-start min-w-0 cursor-pointer"
            onClick={handleClick}
          >
            <div className="shrink-0 w-[36px] h-[36px] overflow-hidden mr-3">
              <img className="w-full h-full" src={product.thumbnailSmUrl} />
            </div>
            <div className="py-1 text-sm tracking-tighter truncate">
              {commissionPrice > 0 ? (
                <p className="text-xs tracking-tighter truncate text-[#FF0099]">
                  {`${languageTexts.campaign_with_commissions}` +
                    ` (${HELPER_CURRENCY.makeMoneyText(
                      commissionPrice, 10000,
                      (moneyText) => languageTexts._price(moneyText, props.settings.currencyText),
                      (moneyText) => languageTexts._price_10000(moneyText, props.settings.currencyText),
                    )})`}
                </p>
              ) : null}
              <p className="whitespace-pre-wrap">
                {HELPER_UI.makeProductName(
                  product.name,
                  product?.campaign?.preferredVideoTypeGuidelineRules,
                  product?.campaign
                )}
              </p>
              <p className="truncate">{`${product.colorName} / ${
                product.sizeName
              } / ${languageTexts._amount(product.qty)}`}</p>
              {!product.selectedAt && (
                <p>{`${languageTexts.applicants_count} : ${props.brand.isCategoryBeauty ? product.applicantsCountBeauty : product.applicantsCount}`}</p>
              )}
            </div>
          </div>
          {/* <div className="p-1">{renderPayments()}</div> */}
        </div>

        <div
          className="mt-4 pt-4 border-t text-center cursor-pointer"
          onClick={handleClick}
        >
          {renderCampaignStatus(product.campaign, product)}
        </div>
      </div>
    );
  };

  return (
    <>
      {!preferences.isMobile && renderDesktopProduct()}
      {preferences.isMobile && renderMobileProduct()}
    </>
  );
};

const CampaignDateGroup = (props) => {
  const { products, saveCurrentState, selected, setSelected, fetchProducts } =
    props;

  const [isOpened, setIsOpened] = useState<boolean>(true);
  const toggleAccordion = () => {
    setIsOpened(!isOpened);
  };

  return (
    <div className={clsx([`${props.className}`, 'flex flex-col'])}>
      <div className="flex flex-row justify-between items-center py-6">
        <div className="flex flex-col">
          <span className="font-bold text-xl tracking-tighter">
            {props.date}
          </span>
        </div>
        <div
          className="flex flex-col"
          onClick={() => {
            toggleAccordion();
          }}
        >
          <div
            className={clsx({
              'flex w-7 h-7 sm:w-10 sm:h-10 rounded-full overflow-hidden text-white justify-center items-center cursor-pointer':
                true,
              'bg-gray-400': isOpened,
              'bg-black': !isOpened,
            })}
          >
            {isOpened ? (
              <ChevronUp className="w-3/5" />
            ) : (
              <ChevronDown className="w-3/5" />
            )}
          </div>
        </div>
      </div>
      <Collapsible
        trigger={''}
        open={isOpened}
        transitionTime={300}
        easing={'ease'}
        onClick={() => {
          toggleAccordion();
        }}
      >
        <div className="flex flex-col gap-5">
          {products.map((product) => {
            if (!product.isUse) return null;
            return (
              <CampaignProduct
                {...props}
                preferences={props.preferences}
                product={product}
                selected={selected}
                key={`CampaignProduct${uniqueId()}`}
                onClick={() => {
                  saveCurrentState(
                    ROUTES.CAMPAIGN_PRODUCT_SHOW.SHOW(
                      product.campaignId,
                      product.id,
                    ),
                  );
                }}
                fetchProducts={fetchProducts}
                onSelect={(selectedItem) => {
                  if (selected.includes(selectedItem.id)) {
                    setSelected(
                      selected.filter((item) => {
                        return item != selectedItem.id;
                      }),
                    );
                  } else {
                    setSelected([...selected, selectedItem.id]);
                  }
                }}
              />
            );
          })}
        </div>
      </Collapsible>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(CampaignIndex);
